import { InfoOutlined } from '@mui/icons-material';
import {
  Box,
  Chip,
  IconButton,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { memo, useLayoutEffect, useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { FormErrorText } from '../../forms';
import {
  flattenKeyedOptions,
  getAggregateSentimentForCheckbox,
  getExpectedAnswerString,
  getQuestionInputKind,
  getSentimentForYesNoSelection,
  normalizeCheckboxAnswer,
} from './utils/qcAssessmentQuestionUtils';

function formatOptionLabel(label) {
  const s = String(label);
  if (s.length <= 40) return s;
  return `${s.slice(0, 37)}…`;
}

/** Green / red inset highlight from option `sentiment`; none if unknown or unset. */
function sentimentHighlightSx(sentiment) {
  if (sentiment !== 'positive' && sentiment !== 'negative') return {};
  return (theme) => {
    const isNeg = sentiment === 'negative';
    const main = isNeg ? theme.palette.error.main : theme.palette.success.main;
    return {
      borderRadius: theme.spacing(1),
      px: { xs: 0.75, sm: 1, md: 1.125, lg: 1.25 },
      py: { xs: 0.5, sm: 0.5, md: 0.625, lg: 0.625 },
      boxSizing: 'border-box',
      bgcolor: alpha(main, isNeg ? 0.08 : 0.1),
      boxShadow: `inset 0 0 0 1px ${alpha(main, 0.38)}`,
    };
  };
}

/**
 * One line: "Q {n}: {title}" with red * immediately after the title when required;
 * info icon + tooltip when truncated.
 */
const QuestionTitleOneLine = memo(function QuestionTitleOneLine({
  questionNumber,
  title,
  required,
  tooltipTitle,
  isMobile,
}) {
  const textRef = useRef(null);
  const [overflows, setOverflows] = useState(false);

  const fullLabel = `Q ${questionNumber} : ${title}`;

  useLayoutEffect(() => {
    const el = textRef.current;
    if (!el) return;

    const measure = () => {
      setOverflows(el.scrollWidth > el.clientWidth + 1);
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [questionNumber, title, required]);

  const tip = tooltipTitle ?? `${fullLabel}${required ? ' * ' : ''}`;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        minWidth: 0,
        width: '100%',
      }}
    >
      <Typography
        ref={textRef}
        component="span"
        sx={{
          flex: 1,
          minWidth: 0,
          fontSize: isMobile ? '0.875rem' : '0.8125rem',
          fontWeight: 500,
          lineHeight: 1.35,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          color: 'text.primary',
        }}
      >
        <Box
          component="span"
          sx={{ display: 'inline', color: 'text.primary' }}
        >{`Q ${questionNumber}: `}</Box>
        <Box component="span" sx={{ display: 'inline', color: 'text.primary' }}>
          {title}
        </Box>
        {required ? (
          <Box
            component="span"
            aria-hidden
            sx={(theme) => ({
              display: 'inline',
              color: theme.palette.error.main,
              fontWeight: 900,
            })}
          >
            *
          </Box>
        ) : null}
      </Typography>
      {overflows ? (
        <Tooltip
          arrow
          placement="top"
          enterTouchDelay={0}
          slotProps={{
            tooltip: {
              sx: {
                maxWidth: { xs: 'min(90vw, 360px)', sm: 420 },
                whiteSpace: 'normal',
                fontSize: '0.8125rem',
                lineHeight: 1.45,
              },
            },
          }}
          title={tip}
        >
          <IconButton
            size="small"
            aria-label="Show full question"
            sx={(theme) => ({
              flexShrink: 0,
              p: 0.35,
              color: theme.palette.action.active,
              '&:hover': {
                color: theme.palette.primary.main,
                bgcolor: alpha(theme.palette.primary.main, 0.12),
              },
            })}
          >
            <InfoOutlined sx={{ fontSize: isMobile ? 18 : 16 }} />
          </IconButton>
        </Tooltip>
      ) : null}
    </Box>
  );
});

/** Renders below the question when `property.expected_answer` is set. */
const QuestionExpectationLine = memo(function QuestionExpectationLine({
  expectedAnswer,
}) {
  const trimmed =
    typeof expectedAnswer === 'string' ? expectedAnswer.trim() : '';
  if (!trimmed) return null;
  return (
    <Typography
      component="div"
      sx={{
        mt: { xs: 0.25, sm: 0.25, md: 0.375 },
        fontSize: { xs: '0.7rem', sm: '0.72rem', md: '0.75rem' },
        lineHeight: 1.45,
        fontWeight: 500,
        minWidth: 0,
        wordBreak: 'break-word',
      }}
    >
      <Box
        component="span"
        sx={{
          fontWeight: 600,
          color: 'text.secondary',
        }}
      >
        Expected :
      </Box>{' '}
      <Box
        component="span"
        sx={{
          fontWeight: 700,
          color: 'primary.main',
        }}
      >
        {trimmed}
      </Box>
    </Typography>
  );
});

const YesOrNoInput = memo(function YesOrNoInput({
  question,
  value,
  onChange,
  readOnly,
  compact,
  desktopPill,
}) {
  const options = flattenKeyedOptions(
    question?.options?.yesOrNo ?? question?.options?.radio,
  );
  if (options.length === 0) return null;

  const normalized = String(value || '').toLowerCase();
  const selected =
    options.find((o) => o.label.toLowerCase() === normalized)?.label ?? '';
  const sentiment = getSentimentForYesNoSelection(options, selected);

  return (
    <Box
      sx={[
        {
          display: 'inline-flex',
          maxWidth: '100%',
          flexWrap: 'wrap',
          justifyContent: 'flex-end',
        },
        sentimentHighlightSx(sentiment),
      ]}
    >
      <ToggleButtonGroup
        exclusive
        value={selected}
        onChange={(_, v) => {
          if (v != null && !readOnly) onChange(v);
        }}
        disabled={readOnly}
        size="small"
        sx={(theme) => ({
          flexWrap: 'wrap',
          justifyContent: 'flex-end',
          gap: desktopPill ? 0.5 : compact ? 0.5 : 0,
          ...(desktopPill
            ? {
                bgcolor:
                  theme.palette.mode === 'dark'
                    ? 'rgba(255,255,255,0.06)'
                    : '#f1f3f4',
                borderRadius: '8px',
                p: '2px',
                '& .MuiToggleButtonGroup-grouped': { margin: 0 },
                '& .MuiToggleButton-root': {
                  border: 'none',
                  borderRadius: '6px !important',
                  px: 1.5,
                  py: 0.5,
                  fontSize: '11px',
                  fontWeight: 700,
                  color:
                    theme.palette.mode === 'dark'
                      ? 'rgba(255,255,255,0.65)'
                      : '#565c84',
                  textTransform: 'none',
                  borderRight: 'none',
                  '&.Mui-selected': {
                    bgcolor: theme.palette.background.paper,
                    color: '#24389c',
                    boxShadow:
                      theme.palette.mode === 'dark'
                        ? '0px 1px 3px rgba(0,0,0,0.45)'
                        : '0px 1px 3px rgba(0,0,0,0.12)',
                    '&:hover': { bgcolor: theme.palette.background.paper },
                  },
                },
              }
            : {
                '& .MuiToggleButton-root': {
                  fontSize: compact ? '0.65rem' : '0.7rem',
                  fontWeight: 700,
                  textTransform: 'none',
                  px: 1.25,
                  py: 0.75,
                  borderColor: 'rgba(197,197,212,0.45)',
                  ...(theme.palette.mode === 'dark'
                    ? { color: 'rgba(255,255,255,0.82)' }
                    : {}),
                },
              }),
        })}
      >
        {options.map((opt) => (
          <ToggleButton key={opt.key} value={opt.label}>
            {formatOptionLabel(opt.label)}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Box>
  );
});

const CheckboxInput = memo(function CheckboxInput({
  question,
  value,
  onChange,
  readOnly,
}) {
  const options = flattenKeyedOptions(question?.options?.checkBox);
  if (options.length === 0) return null;

  const keySet = new Set(options.map((o) => String(o.key)));
  const resolvedKeys = normalizeCheckboxAnswer(value)
    .map((x) => {
      const xs = String(x);
      if (keySet.has(xs)) return xs;
      const byLabel = options.find(
        (o) => o.label.toLowerCase() === xs.toLowerCase(),
      );
      return byLabel ? String(byLabel.key) : null;
    })
    .filter(Boolean);

  const selectedSet = new Set(resolvedKeys);

  const toggleKey = (key) => {
    if (readOnly) return;
    const k = String(key);
    const next = new Set(resolvedKeys);
    if (next.has(k)) next.delete(k);
    else next.add(k);
    const ordered = options
      .map((o) => String(o.key))
      .filter((id) => next.has(id));
    onChange(ordered);
  };

  const sentiment = getAggregateSentimentForCheckbox(options, resolvedKeys);

  return (
    <Box
      role="group"
      aria-label="Multi-select options"
      sx={[
        {
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: { xs: 0.5, sm: 0.65, md: 0.75, lg: 0.75, xl: 0.75 },
          width: '100%',
          maxWidth: '100%',
          justifyContent: 'flex-start',
        },
        sentimentHighlightSx(sentiment),
      ]}
    >
      {options.map((opt) => {
        const id = String(opt.key);
        const selected = selectedSet.has(id);
        return (
          <Chip
            key={id}
            label={formatOptionLabel(opt.label)}
            title={String(opt.label)}
            onClick={() => toggleKey(opt.key)}
            disabled={readOnly}
            variant={selected ? 'filled' : 'outlined'}
            size="small"
            sx={{
              fontWeight: selected ? 700 : 600,
              fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8125rem' },
              height: { xs: 30, sm: 31, md: 32 },
              minHeight: { xs: 30, md: 32 },
              cursor: readOnly ? 'default' : 'pointer',
              borderColor: 'rgba(197,197,212,0.65)',
              transition:
                'background-color 0.15s, color 0.15s, border-color 0.15s',
              ...(selected
                ? {
                    bgcolor: '#24389c',
                    color: '#fff',
                    borderColor: '#24389c',
                    '&:hover': {
                      bgcolor: readOnly ? '#24389c' : '#1e3a8a',
                    },
                  }
                : {
                    bgcolor: 'background.paper',
                    color: 'text.secondary',
                    '&:hover': {
                      bgcolor: readOnly
                        ? 'background.paper'
                        : 'rgba(36,56,156,0.08)',
                      borderColor: readOnly
                        ? 'rgba(197,197,212,0.65)'
                        : '#24389c',
                    },
                  }),
            }}
          />
        );
      })}
    </Box>
  );
});

/** Shown once below the last question when any item is required. */
function RequiredFieldsFooterNote() {
  return (
    <Typography
      component="div"
      sx={{
        mt: { xs: 1, sm: 1.125, md: 1.25 },
        pt: { xs: 1, sm: 1.125, md: 1.25 },
        px: { xs: 0.25, md: 0.5 },
        pb: { xs: 0.25, md: 0.5 },
        fontSize: {
          xs: '0.75rem',
          sm: '0.8125rem',
          md: '0.8125rem',
          lg: '0.8125rem',
        },
        lineHeight: 1.45,
        color: 'text.secondary',
        fontWeight: 500,
      }}
    >
      <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
        Note :{' '}
      </Box>
      <Box
        component="span"
        sx={(theme) => ({
          color: theme.palette.error.main,
          fontWeight: 700,
        })}
      >
        *
      </Box>
      <Box component="span" sx={{ fontWeight: 600 }}>
        {' '}
        Required Fields
      </Box>
    </Typography>
  );
}

function assessmentRowPropsAreEqual(prev, next) {
  return (
    prev.questionId === next.questionId &&
    prev.idKey === next.idKey &&
    prev.questionNumber === next.questionNumber &&
    prev.title === next.title &&
    prev.required === next.required &&
    prev.expectedAnswer === next.expectedAnswer &&
    prev.kind === next.kind &&
    prev.readOnly === next.readOnly &&
    prev.isMobile === next.isMobile &&
    prev.isDesktop === next.isDesktop &&
    prev.question === next.question &&
    prev.isLinkedText === next.isLinkedText &&
    prev.linkedTranscriptQuestionIds === next.linkedTranscriptQuestionIds &&
    prev.onPlainTranscriptFromLinkedField ===
      next.onPlainTranscriptFromLinkedField &&
    prev.onLinkedTranscriptFieldCommitted ===
      next.onLinkedTranscriptFieldCommitted
  );
}

const AssessmentQuestionRow = memo(function AssessmentQuestionRow({
  questionId,
  idKey,
  question,
  questionNumber,
  title,
  required,
  expectedAnswer,
  kind,
  readOnly,
  isMobile,
  isDesktop,
  isLinkedText,
  linkedTranscriptQuestionIds,
  onPlainTranscriptFromLinkedField,
  onLinkedTranscriptFieldCommitted,
}) {
  const { control, setValue } = useFormContext();
  const fieldName = `answers.${idKey}`;
  const errorId = `qc-aq-${idKey}-err`;

  const isTextAnswer = kind === 'textAnswer';
  const isCheckBox = kind === 'checkBox';

  const textAnswerField = isTextAnswer ? (
    <Controller
      name={fieldName}
      control={control}
      render={({ field, fieldState }) => (
        <Box sx={{ width: '100%', minWidth: 0 }}>
          <TextField
            {...field}
            value={String(field.value ?? '')}
            onChange={(e) => {
              const v = e.target.value;
              field.onChange(v);
              if (isLinkedText) {
                setValue('updatedTranscript', v, {
                  shouldValidate: false,
                  shouldDirty: true,
                });
                for (const lid of linkedTranscriptQuestionIds) {
                  setValue(`answers.${String(lid)}`, v, {
                    shouldValidate: false,
                    shouldDirty: true,
                  });
                }
                onPlainTranscriptFromLinkedField?.(v);
              }
            }}
            onBlur={(e) => {
              field.onBlur(e);
              if (isLinkedText) onLinkedTranscriptFieldCommitted?.();
            }}
            inputProps={{
              'aria-invalid': fieldState.invalid,
              'aria-describedby': fieldState.error ? errorId : undefined,
              // Helps some IME / transliteration tools (e.g. Google Input Tools) behave with MUI inputs.
              autoComplete: 'off',
            }}
            error={Boolean(fieldState.error)}
            fullWidth
            size="small"
            multiline
            minRows={2}
            maxRows={2}
            placeholder="Type…"
            slotProps={{ input: { readOnly } }}
            sx={{
              width: '100%',
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                fontSize: {
                  xs: '0.8125rem',
                  sm: '0.8125rem',
                  md: '0.8125rem',
                },
                alignItems: 'flex-start',
              },
              '& .MuiOutlinedInput-input': {
                overflowY: 'auto !important',
                resize: 'none',
                lineHeight: 1.43,
                py: 0.75,
              },
            }}
          />
          <FormErrorText id={errorId} message={fieldState.error?.message} />
        </Box>
      )}
    />
  ) : null;

  const checkboxControl = isCheckBox ? (
    <Controller
      name={fieldName}
      control={control}
      render={({ field, fieldState }) => (
        <Box sx={{ width: '100%', minWidth: 0 }}>
          <CheckboxInput
            question={question}
            value={Array.isArray(field.value) ? field.value : []}
            onChange={field.onChange}
            readOnly={readOnly}
          />
          <FormErrorText message={fieldState.error?.message} />
        </Box>
      )}
    />
  ) : null;

  const questionControls = (
    <>
      {kind === 'yesOrNo' ? (
        <Controller
          name={fieldName}
          control={control}
          render={({ field, fieldState }) => (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: { xs: 'stretch', sm: 'flex-end' },
                gap: 0.25,
                minWidth: 0,
              }}
            >
              <YesOrNoInput
                question={question}
                value={field.value}
                onChange={field.onChange}
                readOnly={readOnly}
                compact={isMobile}
                desktopPill={isDesktop}
              />
              <FormErrorText message={fieldState.error?.message} />
            </Box>
          )}
        />
      ) : null}

      {isTextAnswer ? textAnswerField : null}

      {checkboxControl}

      {kind === 'unknown' ? (
        <Typography
          sx={{ fontSize: '0.75rem', color: '#ba1a1a', maxWidth: 280 }}
        >
          Unsupported question type.
        </Typography>
      ) : null}
    </>
  );

  const rowAlign =
    kind === 'yesOrNo' &&
    flattenKeyedOptions(question?.options?.yesOrNo ?? question?.options?.radio)
      .length > 6
      ? 'flex-start'
      : 'center';

  if (isMobile) {
    return (
      <Box className="flex flex-col gap-2 rounded-lg border border-[rgba(197,197,212,0.3)] bg-white p-3 shadow-sm dark:border-gray-700 dark:bg-gray-900">
        <Box sx={{ minWidth: 0, width: '100%' }}>
          <QuestionTitleOneLine
            questionNumber={questionNumber}
            title={title}
            required={required}
            isMobile
          />
          <QuestionExpectationLine expectedAnswer={expectedAnswer} />
        </Box>
        <Box sx={{ width: '100%' }}>{questionControls}</Box>
      </Box>
    );
  }

  if (isTextAnswer) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          gap: { xs: 1, sm: 1, md: 1.125, lg: 1.25 },
          py: { xs: 1, md: 1.25 },
          px: { xs: 0.25, md: 0.5 },
          position: 'relative',
          zIndex: 0,
          isolation: 'isolate',
        }}
      >
        <Box sx={{ minWidth: 0, width: '100%' }}>
          <QuestionTitleOneLine
            questionNumber={questionNumber}
            title={title}
            required={required}
            isMobile={false}
          />
          <QuestionExpectationLine expectedAnswer={expectedAnswer} />
        </Box>
        <Box sx={{ width: '100%', minWidth: 0 }}>{textAnswerField}</Box>
      </Box>
    );
  }

  if (isCheckBox) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          gap: { xs: 1, sm: 1, md: 1.125, lg: 1.25 },
          py: { xs: 1, md: 1.25 },
          px: { xs: 0.25, md: 0.5 },
          position: 'relative',
          zIndex: 0,
          isolation: 'isolate',
        }}
      >
        <Box sx={{ minWidth: 0, width: '100%' }}>
          <QuestionTitleOneLine
            questionNumber={questionNumber}
            title={title}
            required={required}
            isMobile={false}
          />
          <QuestionExpectationLine expectedAnswer={expectedAnswer} />
        </Box>
        <Box sx={{ width: '100%', minWidth: 0 }}>{checkboxControl}</Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: rowAlign,
        justifyContent: 'space-between',
        gap: 1.25,
        py: 1.25,
        px: 0.5,
        minHeight: 44,
        position: 'relative',
        zIndex: 0,
        isolation: 'isolate',
      }}
    >
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          pr: 1,
          alignSelf: rowAlign === 'flex-start' ? 'flex-start' : 'center',
          pt: rowAlign === 'flex-start' ? 0.5 : 0,
        }}
      >
        <QuestionTitleOneLine
          questionNumber={questionNumber}
          title={title}
          required={required}
          isMobile={false}
        />
        <QuestionExpectationLine expectedAnswer={expectedAnswer} />
      </Box>
      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          alignItems: rowAlign === 'flex-start' ? 'flex-start' : 'center',
          justifyContent: 'flex-end',
          minWidth: { md: 120 },
          maxWidth: { md: 520 },
        }}
      >
        {questionControls}
      </Box>
    </Box>
  );
}, assessmentRowPropsAreEqual);

export {
  CheckboxInput,
  QuestionExpectationLine,
  QuestionTitleOneLine,
  YesOrNoInput,
};

/**
 * @param {object} props
 * @param {Set<string>} [props.linkedTranscriptQuestionIdSet] — `link_transcript` text questions (must be inside `FormProvider`).
 * @param {(string|number)[]} [props.linkedTranscriptQuestionIds] — all linked question ids for two-way sync.
 * @param {(plain: string) => void} [props.onPlainTranscriptFromLinkedField] — rebuild chip segments when linked text field is edited.
 * @param {() => void} [props.onLinkedTranscriptFieldCommitted] — blur on `link_transcript` text field (recompute change %).
 */
export default function DynamicAssessmentQuestions({
  questions,
  readOnly,
  variant = 'desktop',
  linkedTranscriptQuestionIdSet,
  linkedTranscriptQuestionIds = [],
  onPlainTranscriptFromLinkedField,
  onLinkedTranscriptFieldCommitted,
}) {
  const isMobile = variant === 'mobile';
  const isDesktop = variant === 'desktop';

  if (!Array.isArray(questions) || questions.length === 0) {
    return (
      <Typography sx={{ color: 'text.secondary', fontSize: '0.875rem', py: 2 }}>
        No assessment questions for this request.
      </Typography>
    );
  }

  const hasAnyRequired = questions.some((q) => Boolean(q?.property?.required));

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        ...(isDesktop
          ? {
              gap: 0,
              '& > *:not(:last-of-type)': {
                borderBottom: '1px solid',
                borderColor: 'divider',
              },
            }
          : { gap: 1.25 }),
      }}
    >
      {questions.map((q, idx) => {
        const qid =
          q?.question_id === undefined || q?.question_id === null
            ? idx
            : q.question_id;
        const idKey = String(qid);
        const title = q?.question_text ?? q?.question_name ?? 'Q';
        const required = Boolean(q?.property?.required);
        const kind = getQuestionInputKind(q);

        const questionNumber = idx + 1;
        const expectedAnswer = getExpectedAnswerString(q);
        const isLinkedText =
          kind === 'textAnswer' &&
          linkedTranscriptQuestionIdSet instanceof Set &&
          linkedTranscriptQuestionIdSet.has(idKey);

        return (
          <AssessmentQuestionRow
            key={idKey}
            questionId={qid}
            idKey={idKey}
            question={q}
            questionNumber={questionNumber}
            title={title}
            required={required}
            expectedAnswer={expectedAnswer}
            kind={kind}
            readOnly={readOnly}
            isMobile={isMobile}
            isDesktop={isDesktop}
            isLinkedText={isLinkedText}
            linkedTranscriptQuestionIds={linkedTranscriptQuestionIds}
            onPlainTranscriptFromLinkedField={onPlainTranscriptFromLinkedField}
            onLinkedTranscriptFieldCommitted={onLinkedTranscriptFieldCommitted}
          />
        );
      })}
      {hasAnyRequired ? <RequiredFieldsFooterNote /> : null}
    </Box>
  );
}
